import { useBreakpointDown } from '@travel-frontend/core';
import { isEqual } from 'lodash';
import { useState } from 'react';
import * as ActionTypes from '../../../../../components/SearchForm/components/TravellersField/actionTypes.js';
import { useWidgetOverlay } from '../../../../../components/SearchForm/hooks/useWidgetOverlay.js';
export const useTravellersField = ({
  value: controlledValue,
  defaultValue,
  config: {
    resetBehavior = 'previous',
    required,
    adultCountMin,
    adultCountMax,
    childrenAgeMin,
    childrenAgeMax,
    childrenCountMin,
    childrenCountMax,
    maxTravellers,
    multiRoomBooking,
    widgetHead
  },
  floatingOptions,
  invalid = false,
  disabled = false,
  dirty = false,
  layout = 'full',
  dimming = false,
  dispatch
}) => {
  const mdDown = useBreakpointDown('md');
  const [value, setValue] = useState(controlledValue);
  const widgetType = mdDown ? 'drawer' : 'popover';
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [maxAllowedValues, setMaxAllowedValues] = useState({
    adults: adultCountMax,
    children: childrenCountMax
  });
  const calculateMaxAllowedValues = (adultCount, childrenCount) => ({
    adults: adultCount === adultCountMax ? adultCountMax : maxTravellers - childrenCount,
    children: childrenCount === childrenCountMax ? childrenCountMax : maxTravellers - adultCount
  });
  const handleOpenChange = (open, origin) => {
    if (origin === 'close-button' && didValueChange()) {
      dispatchEvent({
        type: ActionTypes.CloseClicked,
        value: controlledValue
      });
      setValue(controlledValue);
    }
    if (didValueChange() && (origin === 'apply-button' || !open && widgetType === 'popover')) {
      // eslint-disable-next-line no-var
      var {
        isDefaultPrevented
      } = dispatchEvent({
        type: ActionTypes.ValueChanged,
        value
      });
    }
    // @ts-expect-error FIXME
    if (!isDefaultPrevented) {
      setWidgetOpen(open);
    }
  };
  const overlay = useWidgetOverlay({
    open: widgetOpen,
    type: widgetType,
    options: floatingOptions,
    dimming,
    onOpenChange: handleOpenChange,
    layout
  });
  const handleApply = () => {
    handleOpenChange(false, 'apply-button');
  };
  const handleClose = () => {
    handleOpenChange(false, 'close-button');
  };
  const handleReset = () => {
    dispatchEvent({
      type: ActionTypes.ResetClicked,
      value: controlledValue
    });
    setValue(resetBehavior === 'default' && defaultValue ? defaultValue : controlledValue);
  };
  const handleAdultCountChange = adultCount => {
    const childrenAges = value?.childrenAges;
    const newValue = {
      adultCount,
      childrenAges: childrenAges ?? []
    };
    dispatchEvent({
      type: ActionTypes.AdultCountChanged,
      value: newValue
    });
    setValue(newValue);
    setMaxAllowedValues(calculateMaxAllowedValues(adultCount, childrenAges?.length ?? 0));
  };
  const handleChildrenCountChange = childrenCount => {
    const adultCount = value?.adultCount;
    const newValue = {
      adultCount,
      childrenAges: Array.from({
        length: childrenCount
      }, (_, index) => value?.childrenAges[index] ?? null)
    };
    dispatchEvent({
      type: ActionTypes.ChildrenCountChanged,
      value: newValue
    });
    setValue(newValue);
    setMaxAllowedValues(calculateMaxAllowedValues(adultCount ?? 0, childrenCount));
  };
  const handleChildAgeChange = (childAge, index) => {
    const newValue = {
      adultCount: value?.adultCount,
      childrenAges: Array.from({
        length: value?.childrenAges.length ?? 0
      }, (_, index_) => index === index_ ? childAge : value?.childrenAges[index_] ?? null)
    };
    dispatchEvent({
      type: ActionTypes.ChildrenAgeChanged,
      value: newValue
    });
    setValue(newValue);
  };
  const areValuesEqual = (valueA, valueB) => {
    return valueA?.adultCount === valueB?.adultCount && isEqual(valueA?.childrenAges, valueB?.childrenAges) // Note: different order is considered a change!
    ;
  };
  const didValueChange = () => {
    return !areValuesEqual(value, controlledValue);
  };
  const isValidChildrenAge = childrenAge => {
    return childrenAge != null && childrenAge >= childrenAgeMin && childrenAge <= childrenAgeMax;
  };
  const isValidValue = value => {
    const childrenAges = value?.childrenAges ?? [];
    return value?.adultCount != null && value.adultCount >= adultCountMin && value.adultCount <= adultCountMax && childrenAges.length >= childrenCountMin && childrenAges.length <= childrenCountMax && childrenAges.every(isValidChildrenAge);
  };
  const dispatchEvent = ({
    type,
    value
  }) => {
    let isDefaultPrevented = false;
    dispatch({
      type,
      payload: {
        value,
        valid: isValidValue(value)
      },
      preventDefault: () => {
        isDefaultPrevented = true;
      }
    });
    return {
      isDefaultPrevented
    };
  };
  const getFieldProps = () => ({
    ...overlay.getReferenceProps(),
    value,
    active: widgetOpen,
    invalid,
    disabled,
    canHaveShortLabel: !mdDown
  });
  const getWidgetProps = () => ({
    ...overlay.getWidgetProps(),
    type: widgetType,
    open: widgetOpen,
    data: value,
    maxAllowedValues,
    hasMissingChildrenAgeMessage: invalid,
    childrenAgesValidationState: invalid ? value?.childrenAges.map(isValidChildrenAge) : [],
    applyButtonDisabled: dirty,
    handlers: {
      onReset: handleReset,
      onApply: handleApply,
      onClose: handleClose,
      onAdultCountChange: handleAdultCountChange,
      onChildAgeChange: handleChildAgeChange,
      onChildrenCountChange: handleChildrenCountChange
    },
    config: {
      adultCountMin,
      adultCountMax,
      childrenCountMin,
      childrenCountMax,
      childrenAgeMin,
      childrenAgeMax,
      multiRoomBooking,
      maxTravellers
    },
    widgetHead,
    layout,
    dimming
  });
  const setValueHandler = nextValue => {
    if (!areValuesEqual(value, nextValue)) {
      dispatchEvent({
        type: ActionTypes.FunctionValueChanged,
        value: nextValue
      });
      setValue(nextValue);
    }
  };
  const checkValidity = () => required === false || isValidValue(value);
  return {
    isActive: widgetOpen,
    isInvalid: invalid,
    isDisabled: disabled,
    getFieldProps,
    getWidgetProps,
    setValue: setValueHandler,
    checkValidity
  };
};